<template>
  <div class="header-fixed" :class="{ collapsed: collapsed }">
    <div>
      <a-icon class="trigger" :type="collapsed ? 'menu-unfold' : 'menu-fold'" @click="$emit('toggleCollapsed')" />
    </div>
    <div>
      <a-popover overlayClassName="notification-popover" placement="bottomRight" trigger="click">
        <template slot="content">
          <div class="notification-panel">
            <div class="notification-panel__header">
              <span class="notification-panel__title">消息通知</span>
              <span class="notification-panel__count">{{ infoAccount }} 条未读</span>
            </div>
            <div class="notification-panel__body">
              <button
                v-for="val in messageList"
                :key="val.source + '-' + (val.notice_id || val.title)"
                type="button"
                class="notice-item"
                :class="{ 'notice-item--unread': !val.is_read, 'notice-item--read': val.is_read }"
                @click="onTxt(val)"
              >
                <span v-if="!val.is_read" class="notice-dot" aria-label="未读"></span>
                <span class="notice-item__main">
                  <span class="notice-title">{{ val.title }}</span>
                  <span class="notice-summary">{{ val.summary || val.content || '暂无内容摘要' }}</span>
                  <span class="notice-meta">
                    <span>{{ val.publisher || '系统通知' }}</span>
                    <span>{{ val.publish_time || '-' }}</span>
                  </span>
                </span>
              </button>
              <a-empty v-if="messageList.length === 0" class="notification-empty" description="暂无通知" />
            </div>
            <button type="button" class="notification-panel__footer" @click="$router.push('/notification')">
              查看全部通知
              <a-icon type="right" />
            </button>
          </div>
        </template>
        <a-badge :count="infoAccount" style="margin-right: 24px;">
          <a-icon style="font-size: 28px" type="bell" />
        </a-badge>
      </a-popover>

      <a-drawer :title="info.title" :visible="visible" width="560" @close="visible = false">
        <div class="drawer-meta">
          <a-tag :color="info.level === 'warning' ? 'orange' : 'blue'">{{ typeText(info.type) }}</a-tag>
          <span>{{ info.publisher || '系统通知' }}</span>
          <span>{{ info.pub_time || '-' }}</span>
        </div>
        <p class="drawer-content">{{ info.details }}</p>
      </a-drawer>

      <a-tooltip v-if="impersonation && impersonation.enabled">
        <template slot="title">
          管理员：{{ impersonation.admin_username || '-' }}；目标用户：{{ impersonation.target_username || '-' }}；有效期：{{ impersonation.session_expires_at || impersonation.expires_at || '-' }}
        </template>
        <a-tag class="impersonation-tag" color="orange">正在代登：{{ impersonation.target_username || '-' }}</a-tag>
      </a-tooltip>

      <a-dropdown :trigger="['click']">
        <span class="trigger">
          {{ displayUsername }}
          <a-icon type="down" />
        </span>
        <a-menu slot="overlay">
          <a-menu-item @click="$router.push('/user/set_password')">
            <span>修改密码</span>
          </a-menu-item>
          <a-menu-divider />
          <a-menu-item @click="logout">
            <span>退出登录</span>
          </a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie';
import {
  getNotifications,
  getNotificationUnreadCount,
  readNotification,
  readSystemNotification,
} from '@/api/notification';
import {
  emitNotificationChanged,
  onNotificationChanged,
} from '@/utils/notification-events';
import { revokeImpersonationTicket } from '@/api/impersonation';
const { resolveImpersonationSession } = require('@/utils/impersonation-session');

const NOTIFICATION_REFRESH_INTERVAL = 30000;
const IMPERSONATION_KEY = 'erp_impersonation';
const IMPERSONATION_ACCESS_KEY = 'erp_impersonation_access';
const IMPERSONATION_REFRESH_KEY = 'erp_impersonation_refresh';

export default {
  name: 'Headbar',
  inject: ['reloadPage'],
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    username: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      infoAccount: 0,
      info: {},
      visible: false,
      timer: null,
      unsubscribeNotificationChanged: null,
      messageList: [],
      impersonation: null,
      warehouseItems: [],
      currentWarehouse: '所有仓库',
    };
  },
  computed: {
    displayUsername() {
      if (this.impersonation && this.impersonation.target_username) {
        return this.impersonation.target_username;
      }
      return this.username || localStorage.getItem('username') || '用户';
    },
  },
  created() {
    this.initData();
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.unsubscribeNotificationChanged) {
      this.unsubscribeNotificationChanged();
    }
  },
  methods: {
    fetchMessages() {
      Promise.all([
        getNotifications({ page: 1, page_size: 8 }),
        getNotificationUnreadCount(),
      ]).then(([listRes, countRes]) => {
        const pageData = listRes && listRes.data ? listRes.data : {};
        this.messageList = Array.isArray(pageData.data) ? pageData.data : [];
        this.infoAccount = countRes && countRes.data ? countRes.data.count || 0 : 0;
      }).catch(() => {});
    },
    initData() {
      this.loadImpersonation();
      this.fetchMessages();
      this.unsubscribeNotificationChanged = onNotificationChanged(() => {
        this.fetchMessages();
      });
      this.timer = setInterval(() => { this.fetchMessages(); }, NOTIFICATION_REFRESH_INTERVAL);
    },
    async logout() {
      const isImpersonating = !!this.getValidImpersonation();
      if (isImpersonating) {
        try {
          await revokeImpersonationTicket();
        } catch (e) {
          this.$message.warning('代登撤销失败，本地登录状态已清除');
        }
      }
      if (!isImpersonating) {
        Cookies.remove('access');
        Cookies.remove('refresh');
        localStorage.removeItem('username');
        localStorage.removeItem(IMPERSONATION_KEY);
      }
      sessionStorage.removeItem(IMPERSONATION_ACCESS_KEY);
      sessionStorage.removeItem(IMPERSONATION_REFRESH_KEY);
      sessionStorage.removeItem(IMPERSONATION_KEY);
      if (this.timer) {
        clearInterval(this.timer);
      }
      this.$router.push('/user/login');
    },
    onChangeWarehouse(item) {
      this.currentWarehouse = item.name;
      this.$store.commit('setWarehouse', item.id);
      this.reloadPage();
    },
    loadImpersonation() {
      const session = this.getValidImpersonation();
      this.impersonation = session;
      if (!session) {
        localStorage.removeItem(IMPERSONATION_KEY);
        sessionStorage.removeItem(IMPERSONATION_KEY);
        sessionStorage.removeItem(IMPERSONATION_ACCESS_KEY);
        sessionStorage.removeItem(IMPERSONATION_REFRESH_KEY);
      }
    },
    getValidImpersonation() {
      return resolveImpersonationSession(
        sessionStorage.getItem(IMPERSONATION_ACCESS_KEY) || '',
        sessionStorage.getItem(IMPERSONATION_KEY),
      );
    },
    onTxt(val) {
      if (val.source === 'notice' && !val.is_read && val.notice_id) {
        readNotification(val.notice_id)
          .then(() => {
            this.infoAccount = Math.max(0, this.infoAccount - 1);
            val.is_read = true;
            emitNotificationChanged({ type: 'read', noticeId: val.notice_id });
          })
          .catch(() => {
            this.$message.error('请求出错');
          });
      } else if (val.source === 'system' && val.type === 'merchant_expiry' && !val.is_read && val.read_key) {
        readSystemNotification(val.type, val.read_key)
          .then(() => {
            val.is_read = true;
            emitNotificationChanged({ type: 'system-read', readKey: val.read_key });
          })
          .catch(() => {
            this.$message.error('请求出错');
          });
      }
      this.visible = true;
      this.info = {
        title: val.title,
        details: val.content || val.summary,
        publisher: val.publisher,
        pub_time: val.publish_time,
        type: val.type,
        level: val.level,
      };
    },
    typeText(type) {
      const map = {
        system_update: '系统更新',
        maintenance: '维护通知',
        announcement: '公告',
        merchant_expiry: '到期提醒',
      };
      return map[type] || type || '通知';
    },
  },
};
</script>

<style lang="less">
.notification-popover .ant-popover-inner-content {
  padding: 0;
}
</style>

<style scoped lang="less">
.header-fixed {
  position: fixed;
  top: 0;
  right: 0;
  left: 256px;
  z-index: 1000;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.1s cubic-bezier(0.2, 0, 0, 1);
  height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.header-fixed.collapsed {
  left: 80px;
}

.header-fixed>div:first-child {
  padding-left: 16px;
}

.header-fixed>div:last-child {
  display: flex;
  align-items: center;
  padding-right: 16px;
}

.notification-panel {
  width: 400px;
  max-width: calc(100vw - 32px);
}

.notification-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 18px;
  border-bottom: 1px solid #f0f0f0;
}

.notification-panel__title {
  color: #262626;
  font-size: 16px;
  font-weight: 600;
}

.notification-panel__count {
  color: #8c8c8c;
  font-size: 12px;
}

.notification-panel__body {
  max-height: 420px;
  overflow-x: hidden;
  overflow-y: auto;
}

.notice-item {
  position: relative;
  display: flex;
  width: 100%;
  padding: 14px 18px;
  border: 0;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.notice-item:hover,
.notice-item:focus {
  background: #fafafa;
  outline: none;
}

.notice-item--unread {
  background: #f0f7ff;
}

.notice-item--unread:hover,
.notice-item--unread:focus {
  background: #e6f4ff;
}

.notice-dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  margin: 7px 10px 0 0;
  border-radius: 50%;
  background: #1890ff;
}

.notice-item__main {
  display: block;
  min-width: 0;
  flex: 1;
}

.notice-title,
.notice-summary {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notice-title {
  color: #262626;
  font-size: 14px;
  font-weight: 500;
}

.notice-item--unread .notice-title {
  font-weight: 600;
}

.notice-summary {
  margin-top: 5px;
  color: #595959;
  font-size: 13px;
}

.notice-meta,
.drawer-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 7px;
  color: #8c8c8c;
  font-size: 12px;
}

.notice-item--read .notice-title,
.notice-item--read .notice-summary {
  color: #8c8c8c;
}

.notification-empty {
  padding: 36px 0;
}

.notification-panel__footer {
  width: 100%;
  padding: 12px;
  border: 0;
  background: #fff;
  color: #1890ff;
  text-align: center;
  cursor: pointer;
}

.notification-panel__footer:hover,
.notification-panel__footer:focus {
  background: #fafafa;
  outline: none;
}

.drawer-content {
  margin-top: 16px;
  white-space: pre-wrap;
  line-height: 1.8;
}

.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.impersonation-tag {
  max-width: 260px;
  margin-right: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
